import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GovernanceDataTable } from "@/components/governance/GovernanceDataTable";
import { useGovernance } from "@/hooks/useGovernance";

interface TorListRow {
  committeeId: string;
  committeeName: string;
  committeeLevel: number;
  ownerName: string;
  status: string;
  version: string;
  hasTor: boolean;
}

export default function TorList() {
  const navigate = useNavigate();
  const { getActiveFramework, committees, terms, getMemberById } = useGovernance();
  const activeFramework = getActiveFramework();

  const rows = useMemo(
    () =>
      activeFramework
        ? committees
            .filter((committee) => committee.frameworkId === activeFramework.id)
            .map((committee) => {
              const tor = terms.find((item) => item.committeeId === committee.id);
              const ownerName = getMemberById(committee.ownerId)?.name ?? "Unassigned";
              return {
                committeeId: committee.id,
                committeeName: committee.name,
                committeeLevel: committee.level,
                ownerName,
                status: tor?.status ?? "not_started",
                version: tor?.version ?? "-",
                hasTor: Boolean(tor),
              } satisfies TorListRow;
            })
        : [],
    [activeFramework, committees, terms, getMemberById],
  );

  const columns: ColumnDef<TorListRow>[] = [
    {
      accessorKey: "committeeName",
      header: "Committee",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.committeeName}</div>
          <div className="text-xs text-muted-foreground">Level {row.original.committeeLevel}</div>
        </div>
      ),
    },
    {
      accessorKey: "ownerName",
      header: "Owner",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <Badge variant="secondary">{row.original.status}</Badge>,
    },
    {
      accessorKey: "version",
      header: "Version",
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <Link to={`/governance/tor/${row.original.committeeId}`}>
            <Button size="sm" variant="outline">
              {row.original.hasTor ? "Edit ToR" : "Create ToR"}
            </Button>
          </Link>
          {row.original.hasTor ? (
            <Link to={`/governance/tor/${row.original.committeeId}/review`}>
              <Button size="sm">Review ToR</Button>
            </Link>
          ) : null}
        </div>
      ),
    },
  ];

  if (!activeFramework) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-medium text-foreground">Terms of Reference</h1>
          <p className="text-muted-foreground">
            Manage drafting, preview, and approval of committee ToR documents.
          </p>
        </div>
        <Card className="shadow-card">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <p className="text-lg font-medium">Set up your governance structure first</p>
            <p className="text-sm text-muted-foreground">
              Create your board and committees before managing Terms of Reference.
            </p>
            <Button onClick={() => navigate("/governance/dashboard")}>Go to Governance Structure</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-medium text-foreground">Terms of Reference</h1>
        <p className="text-muted-foreground">
          Manage drafting, preview, and approval of committee ToR documents.
        </p>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Committee ToR Status</CardTitle>
        </CardHeader>
        <CardContent>
          <GovernanceDataTable columns={columns} data={rows} emptyMessage="No committees found for this framework." />
        </CardContent>
      </Card>
    </div>
  );
}
